/**
 * Pricing-Szenarien — benannte Snapshots der Inputs für den Pricing-Kalkulator.
 *
 * Speicherung in localStorage als Array (BETA — Server-Persistierung später).
 * Hook usePricingScenarios() liefert eine reaktive Liste plus CRUD-Aktionen.
 *
 * Daten:
 *   - data.inputs   = alle Felder der PricingInputs
 *   - data.mode     = 'standalone' oder 'family'
 *   - data.parent_sku = nur bei family-Mode, sonst null
 *
 * Reaktivität: setzt CustomEvent dispatch + storage-Event-Listener, sodass
 *   parallele Tabs synchronisiert bleiben.
 */

import { useCallback, useEffect, useState } from 'react'
import type { PricingInputs, PricingMode } from './pricingMath'

export interface PricingScenarioData {
  inputs: PricingInputs
  mode: PricingMode
  parent_sku: string | null
}

export interface PricingScenario {
  id: string
  name: string
  created_at: string
  data: PricingScenarioData
}

const STORAGE_KEY = 'channelboard.pricing-scenarios.v1'
const CHANGE_EVENT = 'channelboard:pricing-scenarios-changed'

function safeParse(raw: string | null): PricingScenario[] {
  if (!raw) return []
  try {
    const obj = JSON.parse(raw)
    return Array.isArray(obj) ? (obj as PricingScenario[]) : []
  } catch {
    return []
  }
}

function readAll(): PricingScenario[] {
  if (typeof window === 'undefined') return []
  return safeParse(window.localStorage.getItem(STORAGE_KEY))
}

function writeAll(list: PricingScenario[]): void {
  if (typeof window === 'undefined') return
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(list))
  window.dispatchEvent(new CustomEvent(CHANGE_EVENT))
}

function randomId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  // Cheap fallback — adequate for localStorage keys, not crypto.
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
}

export function listPricingScenarios(): PricingScenario[] {
  return readAll().sort((a, b) => (a.created_at < b.created_at ? 1 : -1))
}

export function createPricingScenario(name: string, data: PricingScenarioData): PricingScenario {
  const scenario: PricingScenario = {
    id: randomId(),
    name: name.trim() || 'Unbenannt',
    created_at: new Date().toISOString(),
    data,
  }
  writeAll([scenario, ...readAll()])
  return scenario
}

export function deletePricingScenario(id: string): void {
  writeAll(readAll().filter((s) => s.id !== id))
}

export function renamePricingScenario(id: string, name: string): void {
  writeAll(
    readAll().map((s) => (s.id === id ? { ...s, name: name.trim() || s.name } : s)),
  )
}

export interface PricingScenarioActions {
  create: (name: string, data: PricingScenarioData) => PricingScenario
  remove: (id: string) => void
  rename: (id: string, name: string) => void
}

export function usePricingScenarios(): [PricingScenario[], PricingScenarioActions] {
  const [list, setList] = useState<PricingScenario[]>(() => listPricingScenarios())

  useEffect(() => {
    const refresh = () => setList(listPricingScenarios())
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) refresh()
    }
    window.addEventListener('storage', onStorage)
    window.addEventListener(CHANGE_EVENT, refresh)
    return () => {
      window.removeEventListener('storage', onStorage)
      window.removeEventListener(CHANGE_EVENT, refresh)
    }
  }, [])

  const create = useCallback((name: string, data: PricingScenarioData) => {
    return createPricingScenario(name, data)
  }, [])
  const remove = useCallback((id: string) => deletePricingScenario(id), [])
  const rename = useCallback((id: string, name: string) => renamePricingScenario(id, name), [])

  return [list, { create, remove, rename }]
}
